import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { heroSlides } from "@/mocks/videos";

export default function HeroCarousel() {
  const [current, setCurrent] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const timer = useRef<number | null>(null);

  const goTo = useCallback((index: number) => {
    setCurrent((index + heroSlides.length) % heroSlides.length);
  }, []);

  const next = useCallback(() => goTo(current + 1), [current, goTo]);
  const prev = useCallback(() => goTo(current - 1), [current, goTo]);

  useEffect(() => {
    timer.current = window.setInterval(() => {
      setCurrent((c) => (c + 1) % heroSlides.length);
    }, 5000);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, []);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const diff = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(diff) > 50) {
      if (diff < 0) next();
      else prev();
    }
    touchStartX.current = null;
  };

  return (
    <section
      className="relative h-[480px] w-full overflow-hidden md:h-[620px]"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {heroSlides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === current ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
        >
          <img
            src={slide.image}
            alt={`${slide.title} 焦点推荐`}
            title={`${slide.title} 粉色视频推荐`}
            className="h-full w-full object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/40 to-foreground-950/30"></div>
        </div>
      ))}

      <div className="relative z-10 mx-auto flex h-full w-full max-w-[1440px] items-end px-4 pb-16 md:items-center md:px-8 md:pb-0">
        <div key={current} className="animate-fade-up max-w-2xl">
          <span className="inline-flex items-center gap-2 rounded-full bg-primary-500/90 px-3 py-1 text-xs font-medium text-background-50">
            <i className="ri-fire-fill"></i>
            {heroSlides[current].tagline}
          </span>
          <h2 className="mt-4 text-3xl font-black text-background-50 md:text-5xl">
            {heroSlides[current].title}
          </h2>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-background-100 md:text-base">
            {heroSlides[current].subtitle}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${heroSlides[current].videoId}`}
              className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-play-circle-fill text-lg"></i>
              立即观看
            </Link>
            <Link
              to="/category/movie"
              className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 bg-background-50/10 px-6 py-3 text-sm font-semibold text-background-50 backdrop-blur-sm transition-colors hover:bg-background-50/20"
            >
              <i className="ri-compass-3-line text-lg"></i>
              浏览全部
            </Link>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={prev}
        className="absolute left-3 top-1/2 z-20 hidden h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur-sm transition-colors hover:bg-background-50/40 md:left-6 md:flex"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        onClick={next}
        className="absolute right-3 top-1/2 z-20 hidden h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur-sm transition-colors hover:bg-background-50/40 md:right-6 md:flex"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      <div className="absolute bottom-6 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2">
        {heroSlides.map((slide, index) => (
          <button
            key={slide.id}
            type="button"
            onClick={() => goTo(index)}
            className={`h-1.5 cursor-pointer rounded-full transition-all ${
              index === current ? "w-8 bg-primary-500" : "w-3 bg-background-50/50 hover:bg-background-50/80"
            }`}
            aria-label={`切换到 ${slide.title}`}
          ></button>
        ))}
      </div>
    </section>
  );
}